/**
 * Task 222 — Hebrew-term hover cards in the corpus reader.
 *
 * Delegated listeners on the reading pane (no per-term React nodes): when the
 * chapter content is ready, one cheap DOM walk marks every <strong> whose text
 * matches the glossary with `data-gloss` (CSS gives those a quiet dotted-gold
 * affordance). Hovering (or tapping, for touch) such a term shows a fixed
 * positioned card near it; Escape/scroll/leaving the term dismisses. The
 * markdown pipeline is untouched — chat rendering can't be affected.
 */
import { useCallback, useEffect, useMemo, useState, type RefObject } from "react";
import { GLOSSARY, buildGlossaryIndex, type GlossaryEntry } from "./glossary";

export interface TermCardState {
  entry: GlossaryEntry;
  /** Viewport coords of the hovered term's box. */
  x: number;
  top: number;
  bottom: number;
}

export function useTermGlossary(
  containerRef: RefObject<HTMLElement>,
  contentReady: boolean,
): { card: TermCardState | null; dismiss: () => void } {
  const [card, setCard] = useState<TermCardState | null>(null);
  const index = useMemo(() => buildGlossaryIndex(GLOSSARY), []);

  // Mark glossary terms once per content load (affordance + cheap hover lookup).
  useEffect(() => {
    if (!contentReady) return;
    const container = containerRef.current;
    if (container === null || index.size === 0) return;
    for (const el of container.querySelectorAll<HTMLElement>(".creader-md strong")) {
      const key = (el.textContent ?? "").trim().toLowerCase();
      if (index.has(key)) el.setAttribute("data-gloss", key);
    }
  }, [contentReady, containerRef, index]);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;

    const show = (el: HTMLElement): void => {
      const key = el.getAttribute("data-gloss");
      const entry = key !== null ? index.get(key) : undefined;
      if (entry === undefined) return;
      const rect = el.getBoundingClientRect();
      setCard({ entry, x: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom });
    };
    const glossTarget = (e: Event): HTMLElement | null => {
      const t = e.target;
      return t instanceof HTMLElement ? t.closest<HTMLElement>("strong[data-gloss]") : null;
    };

    const onOver = (e: MouseEvent): void => {
      const el = glossTarget(e);
      if (el !== null) show(el);
    };
    const onOut = (e: MouseEvent): void => {
      if (glossTarget(e) !== null) setCard(null);
    };
    // Touch/keyboard path: tap toggles (mouseover fires synthetically on tap
    // too, so the toggle only needs to handle the "already open" case).
    const onClick = (e: MouseEvent): void => {
      const el = glossTarget(e);
      if (el === null) return;
      e.preventDefault();
      setCard((prev) => (prev !== null && prev.entry === index.get(el.getAttribute("data-gloss") ?? "") ? null : prev));
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setCard(null);
    };
    const onScroll = (): void => setCard(null);

    container.addEventListener("mouseover", onOver);
    container.addEventListener("mouseout", onOut);
    container.addEventListener("click", onClick);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    return () => {
      container.removeEventListener("mouseover", onOver);
      container.removeEventListener("mouseout", onOut);
      container.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [containerRef, index]);

  const dismiss = useCallback((): void => setCard(null), []);
  return { card, dismiss };
}
